
/**
 * Calcula a confiança da pronúncia baseada no contexto
 * @param {string} word - Palavra a ser verificada
 * @param {string} transcription - Transcrição completa
 * @param {string[]} words - Array de todas as palavras
 * @param {number} index - Índice da palavra no array
 * @returns {number} Confiança de 0 a 1

/**
 * Verifica a pronúncia das palavras comparando com o histórico de transcrições
 * @param {string[]} words - Array de palavras da frase
 * @param {Array<{text: string, confidence: number, isFinal: boolean}>} transcriptionHistory - Array de objetos de transcrição
 * @param {Function} addTranscription - Função para adicionar transcrição ao histórico (opcional)
 * @returns {Array} Array com status de cada palavra
 */
export default function checkPronunciation(words, transcriptionHistory, addTranscription = null) {
  if (!words || words.length === 0) {
    return [];
  }

  if (!transcriptionHistory || transcriptionHistory.length === 0) {
    return words.map(word => ({
      word,
      status: 'pending',
      confidence: 0,
      attempts: 0
    }));
  }

  // Transcrição linear: junta histórico mas mantém espaços
  // Agora extrai .text de cada objeto
  const transcriptionTexts = transcriptionHistory.map((t) => {
    if (typeof t === 'string') return t;
    if (t && typeof t.text === 'string') return t.text;
    return '';
  });
  const fullTranscription = transcriptionTexts.join(' ').toLowerCase().split(/\s+/);

  let pointer = 0; // posição atual na transcrição

  return words.map(word => {
    const safeWord = typeof word === 'string' ? word : '';
    const wordLower = safeWord.toLowerCase().replace(/[^\w\s]/g, '');
    let status = 'pending';
    let attempts = 0;
    let transcribedWord = null; // Palavra que foi transcrita
    let lastAttemptedWord = null; // Última tentativa antes de dar errado

    // Avança até achar a palavra ou até acabar
    while (pointer < fullTranscription.length) {
      attempts+=1;
      const currentTranscribed = fullTranscription[pointer];
      lastAttemptedWord = currentTranscribed; // Guarda sempre a última

      // Checa se é palavra auto-aprovada (marcada com &)
      if (currentTranscribed.startsWith('&')) {
        const cleanWord = currentTranscribed.substring(1).replace(/[^\w\s]/g, '');
        if (cleanWord === wordLower) {
          status = 'auto-approved';
          transcribedWord = currentTranscribed.substring(1); // Remove o &
          pointer+=1;
          break;
        }
      }

      // Checa palavra normal
      if (currentTranscribed.replace(/[^\w\s]/g, '') === wordLower) {
        status = 'correct';
        transcribedWord = currentTranscribed; // Palavra correta
        pointer+=1; // move para frente
        break;
      } else {
        pointer+=1; // palavra não bate, segue
      }
    }

    if (status === 'pending' && attempts > 0) {
      status = 'incorrect';
      transcribedWord = lastAttemptedWord; // Usa a última palavra tentada como feedback
    }

    // Auto-aprovação após 3 tentativas: injeta palavra marcada com &
    const MAX_ATTEMPTS = 6;
    if (status === 'incorrect' && attempts >= MAX_ATTEMPTS && addTranscription) {
      // Injeta a palavra marcada com & no histórico
      addTranscription({
        text: `&${word}`, // Marca com & para identificar auto-aprovação
        isFinal: true,
        confidence: 1,
        timestamp: Date.now(),
        isFake: true // Marca como injetada
      });
    }

    // Calcula confidence
    let confidence = 0;
    if (status === 'correct') {
      confidence = 1;
    } else if (status === 'auto-approved') {
      confidence = 0.5;
    }

    return {
      word,
      status,
      attempts,
      confidence,
      transcribedWord // Adiciona palavra transcrita ao resultado
    };
  });
}
